import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { finalize } from 'rxjs/operators';
import { ProductDto } from 'src/portal/public-api/models/product-dto';
import { ProductPagedDto } from 'src/portal/public-api/models/product-paged-dto';
import { VendorService } from 'src/portal/public-api/services/vendor.service';

export interface ProductSelectDialogData {
  vendorDomain: string;
  productName?: string;
  pageSize?: number;
}

@Component({
  standalone: true,
  selector: 'product-select-dialog',
  templateUrl: './product-select-dialog.component.html',
  styleUrls: ['./product-select-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatRadioModule,
  ],
})
export class ProductSelectDialogComponent implements OnInit {
  displayedColumns: string[] = ['select', 'name', 'category', 'avgRating', 'reviewCount'];
  dataSource: ProductDto[] = [];
  loading = false;

  totalElements = 0;
  pageIndex = 0;
  pageSize = 10;

  selectedProductId: string | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<ProductSelectDialogComponent, ProductDto | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: ProductSelectDialogData,
    private readonly vendorService: VendorService
  ) {
    this.dialogRef.updateSize('860px', '600px');
    if (data?.pageSize && data.pageSize > 0) {
      this.pageSize = data.pageSize;
    }
  }

  ngOnInit(): void {
    this.loadProducts();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProducts();
  }

  loadProducts(): void {
    if (!this.data?.vendorDomain) {
      this.dataSource = [];
      this.totalElements = 0;
      return;
    }

    this.loading = true;
    this.vendorService
      .getProducts({
        vendorDomain: this.data.vendorDomain,
        productName: this.data.productName,
        page: this.pageIndex,
        size: this.pageSize,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: ProductPagedDto) => {
          this.dataSource = response?.content ?? [];
          this.totalElements = response?.totalElements ?? 0;
          if (!this.dataSource.some((item) => item.id === this.selectedProductId)) {
            this.selectedProductId = null;
          }
        },
        error: () => {
          this.dataSource = [];
          this.totalElements = 0;
          this.selectedProductId = null;
        },
      });
  }

  trackById(_: number, item: ProductDto): string | undefined {
    return item?.id;
  }

  onRowClick(product: ProductDto): void {
    this.selectedProductId = product?.id ?? null;
  }

  onSelect(): void {
    const selected = this.dataSource.find((item) => item.id === this.selectedProductId);
    this.dialogRef.close(selected ?? null);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
